// 导入必要的工具和类型
import { setCookie, removeCookie } from './cookie-utils';
import type { LoginType, Origin } from '../services/api-types';
import type { UserCurrent } from '@/services/personal-center/types';
import type {
    TokenCreateParam,
    TokenCreateResult,
} from '@/services/personal-center/types';

export type { LoginType, Origin, TokenCreateResult, UserCurrent };

const TOKEN_EXPIRE = Number(process.env.NEXT_PUBLIC_TOKEN_EXPIRE || '60'); // 提前60秒过期
const TOKEN_REFRESH = Number(process.env.NEXT_PUBLIC_TOKEN_REFRESH || '120');
const COOKIE_MAX_AGE = parseInt(
    process.env.NEXT_PUBLIC_COOKIE_MAX_AGE || '3600',
);

// export interface TokenCreateParam {
//     loginType: ApiLoginType;
//     username: string;
//     password: string;
//     origin: ApiOrigin;
// }

/**
 * 令牌创建结果类型，包含用户信息和令牌（API：Result）
 */
// export interface TokenCreateResult {
//     expiresIn: number;
//     accessToken: string;
//     token: string;
//     salt: string;
//     expiresAt: number;
// }

/**
 * 认证上下文类型，包含用户信息、令牌和认证相关的方法
 */
export interface AuthContextType {
    tokenCreateResult: TokenCreateResult | null;
    login: (
        params: TokenCreateParam,
    ) => Promise<{ success: boolean; message: string }>;
    logout: () => Promise<boolean>;
    refreshProfile: () => Promise<boolean>;
    resetAuthState: () => void;
    isLoading: boolean;
    profile: UserCurrent | null;
}

const setAuthCookie = (
    token: string,
    maxAge: number = 3600,
    cookieName: string,
): void => {
    'use client';

    const isProduction = process.env.NODE_ENV === 'production';
    setCookie(cookieName, token, {
        maxAge: maxAge,
        path: '/',
        secure: isProduction,
    });
    console.log('Cookie set: auth_token');
};

/**
 * 清除认证cookie
 */
const clearAuthCookie = (): void => {
    'use client';
    removeCookie('auth_token');
    removeCookie('refresh_token');
};

/**
 * 存储令牌到本地存储
 * @param tcResult
 */
export const storeAuth = (tcResult: TokenCreateResult): void => {
    'use client';

    localStorage.setItem('token', JSON.stringify(tcResult));
    // 同时设置cookie
    setAuthCookie(tcResult.accessToken, COOKIE_MAX_AGE, 'auth_token');
    setAuthCookie(tcResult.refreshToken, COOKIE_MAX_AGE, 'refresh_token');
};

/**
 * 清除存储的令牌和认证cookie
 */
export const clearAuth = (): void => {
    'use client';

    localStorage.removeItem('token');
    localStorage.removeItem('last_token_refresh_time');
    clearAuthCookie();
};

/**
 * 获取存储的令牌
 * @returns TokenCreateResult | null
 */
export const getAuth = (): TokenCreateResult | null => {
    'use client';

    const token = localStorage.getItem('token');
    if (token) {
        return JSON.parse(token) as TokenCreateResult;
    }
    return null;
};

/**
 * 判断令牌是否过期
 * @param tcResult
 */
export const isTokenExpired = (tcResult: TokenCreateResult | null): boolean => {
    if (!tcResult) return true;
    const currentTime = Math.floor(Date.now() / 1000); // 当前时间的 Unix 时间戳（秒）
    return currentTime >= tcResult.expiresAt - TOKEN_EXPIRE; // 提前 TOKEN_EXPIRE 秒过期
};

/**
 * 判断是否需要刷新令牌
 * @param tcResult
 */
export const shouldRefreshToken = (
    tcResult: TokenCreateResult | null,
): boolean => {
    if (!tcResult) return false;
    const currentTime = Math.floor(Date.now() / 1000); // 当前时间的 Unix 时间戳（秒）
    return currentTime >= tcResult.expiresAt - TOKEN_REFRESH; // 提前 TOKEN_REFRESH 秒刷新
};
